/* global t, safeArray, num, getAnnualAccountsModel, avstSupplierOpenItems, equityReconRound, equityReconCodeNumber */
// EGENKAPITAL — beslutningsgrunnlag, kapital og eiere, likviditet og kontroller
//
// Egenkapitalen har to halvdeler som svikter hver for seg. Den ene er
// regnestykket: inngående kapital, bevegelsene og utgående. Den andre er
// beslutningen: et utbytte er ikke avstemt fordi debet og kredit går opp — det
// er avstemt når generalforsamlingen har vedtatt det og vedtaket kan vises.
//
// Denne fila eier den andre halvdelen, pluss vurderingen av forsvarlig
// egenkapital og likviditet som følger av den.
//
// Ingen ekstern kilde later som den finnes. Foretaksregisterets registrerte
// aksjekapital hentes ikke av appen, og aksjonærregisteroppgaven heller ikke.
// Beslutningsgrunnlaget er generalforsamlingen slik den er registrert i Plumme
// (`annualMeetingsByYear`) — den samme registreringen protokollen genereres og
// signeres fra — pluss dokumenter byrået har lastet opp på området.

const EQUITY_RECON_PROTOCOL_HINTS = ['protokoll', 'generalforsamling'];

const equityReconDocumentMatch = (documents, areaKey, hints) => safeArray(documents)
  .filter((entry) => String(entry?.areaKey || '') === areaKey)
  .find((entry) => {
    const haystack = (String(entry?.kind || '') + ' ' + String(entry?.fileName || '')).toLocaleLowerCase('nb-NO');
    return hints.some((hint) => haystack.includes(hint));
  }) || null;

// Vedtaket bak årets utdelinger. Generalforsamlingen som er registrert for året
// er den beslutningen appen faktisk har; er den ikke registrert, og ingen
// protokoll er lastet opp, finnes det ikke noe vedtak å avstemme mot.
const equityReconDecision = ({ state, year, documents }) => {
  const meeting = state.annualMeetingsByYear?.[String(year)]?.general || null;
  const date = String(meeting?.date || '').slice(0, 10);
  const protocol = equityReconDocumentMatch(documents, 'egenkapital', EQUITY_RECON_PROTOCOL_HINTS);
  return {
    date: date || null,
    dividend: Math.max(0, num(meeting?.dividend)),
    registered: Boolean(date),
    protocolDocument: protocol,
    documented: Boolean(date) || Boolean(protocol),
    source: date ? t('rce.decision.recorded', 'Generalforsamling registrert i Plumme') : protocol ? t('rce.decision.uploaded', 'Opplastet protokoll') : '',
  };
};

// ------------------------------------------------------------------
// Disponering av forrige års resultat
// ------------------------------------------------------------------
// Tre kolonner som kan svikte hver for seg: hva som ble foreslått, hva som ble
// vedtatt, og hva som er bokført. Foreslått og bokført leser appens egne tall;
// vedtatt kan ikke fylles ut uten en registrert generalforsamling, og står da
// som «Ikke verifisert» i stedet for som et tall som ser kontrollert ut.
const equityReconDisposition = ({ year, decision, transactions }) => {
  const previous = typeof getAnnualAccountsModel === 'function'
    ? getAnnualAccountsModel({ year: year - 1 })
    : null;
  const previousResult = previous ? equityReconRound(previous.incomeStatement?.annualResult) : null;
  const dividendRows = transactions.filter((row) => row.typeKey === 'utbytte');
  const bookedDividend = equityReconRound(dividendRows.reduce((sum, row) => sum - row.amount, 0));
  // Resultatdisponeringen som ikke er utbytte: årsoppgjørsføringene mot
  // egenkapitalen som ikke rører en resultatkonto. Fjorårets resultat ligger
  // allerede i inngående egenkapital — disponeringen fordeler det, den legger
  // ikke resultatet til på nytt.
  const transferRows = transactions.filter(
    (row) => row.typeKey === 'resultatdisponering' && !row.movesResult,
  );
  const bookedTransfer = equityReconRound(transferRows.reduce((sum, row) => sum + row.amount, 0));
  const proposedTransfer = previousResult === null ? null : equityReconRound(previousResult - decision.dividend);

  const row = (key, label, proposed, booked) => ({
    key,
    label,
    proposed,
    decided: decision.registered ? proposed : null,
    booked,
    documented: decision.documented,
  });

  const rows = [
    row('utbytte', t('rce.disp.dividend', 'Ordinært utbytte'), decision.dividend || null, bookedDividend),
    row('annen_egenkapital', t('rce.disp.transfer', 'Overført til annen egenkapital'), proposedTransfer, bookedTransfer),
  ];
  const bookedTotal = equityReconRound(bookedDividend + bookedTransfer);
  const proposedTotal = previousResult;
  return {
    previousYear: year - 1,
    previousResult,
    rows,
    total: row('sum', t('rce.disp.total', 'Sum disponert'), proposedTotal, bookedTotal),
    // Bokføringen kan stemme matematisk uten at vedtaket kan vises. De to sier
    // hver sin ting, og en felles status ville skjult den ene.
    bookedMatchesResult: previousResult !== null && Math.abs(bookedTotal - previousResult) <= 0.5,
    booked: Math.abs(bookedTotal) > 0.5,
    verified: decision.registered && decision.documented,
  };
};

// ------------------------------------------------------------------
// Kapital og eiere
// ------------------------------------------------------------------
const equityReconCapital = ({ state, statement, decision, documents }) => {
  const categoryValue = (key) => {
    const row = statement.find((entry) => entry.key === key);
    return row ? row.bokfort : 0;
  };
  const shareholders = window.PlummeAnnualMeetings?.normalizeShareholders?.(state.shareholders) || [];
  const shares = shareholders.reduce((sum, entry) => sum + num(entry.shares), 0);
  const shareCapital = categoryValue('aksjekapital');
  const scope = window.PlummeAccountingScope?.resolveAccountingScope?.(state.settings || {}) || null;
  return {
    entityFamily: scope?.entityFamily || 'unknown',
    legalFormCode: scope?.legalFormCode || '',
    isLimitedCompany: scope?.entityFamily === 'limited_company',
    shareCapital,
    sharePremium: categoryValue('overkurs'),
    ownShares: categoryValue('egne_aksjer'),
    shareholders,
    shareholderCount: shareholders.length,
    shares,
    // Pålydende regnes bare når aksjeeierboka har antall aksjer. Uten den er en
    // pålydende her et regnestykke uten teller.
    nominal: shares > 0 ? equityReconRound(shareCapital / shares) : null,
    // Foretaksregisterets registrerte aksjekapital hentes ikke. Enhetsregisteret
    // bærer den ikke, og et tall her ville vært bokføringen sammenliknet med seg
    // selv.
    registeredShareCapital: null,
    shareholderRegisterDocument: equityReconDocumentMatch(documents, 'egenkapital', ['aksjeeierbok']),
    articlesDocument: equityReconDocumentMatch(documents, 'egenkapital', ['vedtekt']),
    decision,
  };
};

// ------------------------------------------------------------------
// Forsvarlig egenkapital og likviditet
// ------------------------------------------------------------------
// Tallene er balansens egne. Vurderingen er ikke: forsvarlig egenkapital og
// likviditet ses i sammenheng med virksomhetens risiko, omfang og
// fremtidsutsikter (asl. § 3-4), og det er styret som gjør den vurderingen.
// Skjermen leverer grunnlaget og sier hva grunnlaget ikke dekker.
const equityReconLiquidity = ({ state, trial, range, equity }) => {
  let currentAssets = 0;
  let liquid = 0;
  let shortTermDebt = 0;
  let totalAssets = 0;
  for (const [code, account] of trial) {
    const number = equityReconCodeNumber(code);
    if (number === null) continue;
    const value = num(account.ut);
    if (number >= 1000 && number <= 1999) totalAssets += value;
    if (number >= 1400 && number <= 1999) currentAssets += value;
    if (number >= 1900 && number <= 1999) liquid += value;
    if (number >= 2300 && number <= 2999) shortTermDebt += -value;
  }
  const overdue = typeof avstSupplierOpenItems === 'function'
    ? avstSupplierOpenItems({ referenceDate: range.to, state })
      .filter((item) => item.bucket !== 'Ikke forfalt' && item.bucket !== 'Uten forfallsdato')
    : [];
  const budget = state.budgetsByYear?.[String(range.year)] || null;
  const bankData = safeArray(state.bankStatements).some(
    (statement) => safeArray(statement?.lines).some((line) => {
      const date = String(line?.date || '').slice(0, 10);
      return date >= range.yearFrom && date <= range.to;
    }),
  );
  return {
    totalAssets: equityReconRound(totalAssets),
    currentAssets: equityReconRound(currentAssets),
    liquid: equityReconRound(liquid),
    shortTermDebt: equityReconRound(shortTermDebt),
    workingCapital: equityReconRound(currentAssets - shortTermDebt),
    overdueCount: overdue.length,
    overdueAmount: equityReconRound(overdue.reduce((sum, item) => sum + num(item.amount), 0)),
    equityRatio: totalAssets > 0.5 ? Math.round((equity / totalAssets) * 1000) / 10 : null,
    bankData,
    budgetAvailable: Boolean(budget && Object.keys(budget).length),
    // Appen har ingen likviditetsprognose. Uten den kan fremtidig utvikling ikke
    // vurderes av tallene alene, og det står i stedet for å utelates.
    forecastAvailable: false,
  };
};

// ------------------------------------------------------------------
// Forholdene som krever en vurdering
// ------------------------------------------------------------------
const EQUITY_RECON_FLAG = {
  mangler_beslutning: { get label() { return t('rce.flag.noDecisionBasis', 'Beslutningsgrunnlag mangler'); }, cls: 'error' },
  disponering_ikke_verifisert: { get label() { return t('rce.flag.dispositionUnverified', 'Vedtatt disponering kan ikke verifiseres'); }, cls: 'warning' },
  direktefort: { get label() { return t('avbe.tile.directPosting', 'Direkteføring mot egenkapital'); }, cls: 'warning' },
  eiertransaksjon: { get label() { return t('avbe.tile.ownerTransaction', 'Transaksjon med eier'); }, cls: 'warning' },
  beregningsdifferanse: { get label() { return t('rce.flag.calcDifference', 'Beregningsdifferanse'); }, cls: 'error' },
  bilagsdifferanse: { get label() { return t('avbe.tile.noVoucher', 'Bevegelse uten bilagsdekning'); }, cls: 'error' },
  negativ_egenkapital: { get label() { return t('avbe.tile.negativeEquity', 'Negativ egenkapital'); }, cls: 'error' },
  aksjekapital_mangler: { get label() { return t('avbe.tile.noShareCapital', 'Ingen bokført aksjekapital'); }, cls: 'error' },
  aksjeeierbok_mangler: { get label() { return t('avbe.tile.noShareRegister', 'Aksjeeierbok ikke registrert'); }, cls: 'warning' },
};

const equityReconItems = ({ transactions, statement, accountRows, capital, main, periodLabel }) => {
  const items = [];
  const push = (entry) => items.push({ periodLabel, ...entry });

  for (const row of transactions) {
    const flags = [];
    if (row.needsDecision && !row.documented) flags.push('mangler_beslutning');
    if (row.typeKey === 'direktefort') flags.push('direktefort');
    // Begge retninger flagges. En innbetaling fra eier kan være innskutt
    // kapital, et lån eller en tilbakebetaling, og en utbetaling kan være
    // utbytte, lønn eller lån til aksjonær. Fortegnet avgjør ingen av delene —
    // det er vurderingen som gjør det.
    if (['eiertransaksjon', 'uttak', 'innskudd'].includes(row.typeKey)) flags.push('eiertransaksjon');
    if (!flags.length) continue;
    push({
      id: row.id,
      kind: 'transaksjon',
      title: row.type + ' — ' + (row.description || row.type),
      date: row.date,
      amount: row.amount,
      flags,
      detail: row.categories.join(', ')
        + (row.counters.length ? ' · ' + t('rce.item.counterAccount', 'Motkonto {codes}', { codes: row.counters.map((entry) => entry.code).join(', ') }) : ''),
    });
  }

  for (const row of statement) {
    if (Math.abs(row.difference) <= 0.5) continue;
    push({
      id: 'ek-diff-' + row.key,
      kind: 'oppstilling',
      title: t('rce.item.statementDiff', '{label} — beregnet utgående avviker fra bokført', { label: row.label }),
      date: null,
      amount: row.difference,
      flags: ['beregningsdifferanse'],
      detail: t('rce.item.accounts', 'Kontoer: {codes}', { codes: row.accounts.join(', ') }),
    });
  }

  for (const row of accountRows) {
    if (Math.abs(row.drift) <= 0.5) continue;
    push({
      id: 'ek-drift-' + row.code,
      kind: 'konto',
      title: t('rce.item.driftTitle', '{code} {name} — bevegelsen i hovedboken forklarer ikke saldoendringen', { code: row.code, name: row.name }),
      date: null,
      amount: row.drift,
      flags: ['bilagsdifferanse'],
      detail: t('rce.item.driftDetail', 'Saldobalansen og bilagene er uenige om bevegelsen på kontoen.'),
    });
  }

  if (main.computedEquity < -0.5) {
    push({
      id: 'ek-negativ',
      kind: 'signal',
      title: t('rce.item.negativeTitle', 'Beregnet egenkapital er negativ'),
      date: null,
      amount: main.computedEquity,
      flags: ['negativ_egenkapital'],
      detail: t('rce.item.negativeDetail', 'Negativ egenkapital skal vurderes av styret og dokumenteres.'),
    });
  }

  if (capital.isLimitedCompany && Math.abs(capital.shareCapital) < 0.5) {
    push({
      id: 'ek-aksjekapital',
      kind: 'signal',
      title: t('rce.item.noShareCapitalTitle', 'Ingen bokført aksjekapital på egenkapitalkontoene'),
      date: null,
      amount: 0,
      flags: ['aksjekapital_mangler'],
      detail: t('rce.item.noShareCapitalDetail', 'Et aksjeselskap skal ha bokført aksjekapital.'),
    });
  }

  if (capital.isLimitedCompany && !capital.shareholderCount) {
    push({
      id: 'ek-aksjeeierbok',
      kind: 'signal',
      title: t('rce.item.noRegisterTitle', 'Aksjeeierboka er ikke registrert i Plumme'),
      date: null,
      amount: 0,
      flags: ['aksjeeierbok_mangler'],
      detail: t('rce.item.noRegisterDetail', 'Uten aksjeeierbok kan verken eierskap eller utdelinger kontrolleres mot eierne.'),
    });
  }

  return items;
};

const equityReconCounts = (items) => {
  const counts = { flagged: items.length };
  for (const key of Object.keys(EQUITY_RECON_FLAG)) {
    counts[key] = items.filter((item) => item.flags.includes(key)).length;
  }
  return counts;
};

// ------------------------------------------------------------------
// Datakilder og automatiske kontroller
// ------------------------------------------------------------------
const equityReconSources = ({ decision, capital, disposition, liquidity }) => [
  {
    key: 'hovedbok',
    label: t('rce.src.ledger', 'Hovedbok og saldobalanse'),
    complete: true,
    external: false,
    detail: t('rce.src.ledgerDetail', 'Egenkapitalkontoene med inngående saldo og bevegelsene hittil i år.'),
  },
  {
    key: 'protokoll',
    label: t('rce.src.protocol', 'Generalforsamlingens vedtak'),
    complete: decision.documented,
    external: false,
    detail: decision.documented
      ? decision.source + (decision.date ? ' · ' + decision.date : '')
      : t('rce.src.protocolMissing', 'Verken registrert generalforsamling eller opplastet protokoll for året.'),
  },
  {
    key: 'aksjeeierbok',
    label: t('rce.src.register', 'Aksjeeierbok'),
    complete: capital.shareholderCount > 0 || Boolean(capital.shareholderRegisterDocument),
    external: false,
    detail: capital.shareholderCount
      ? t('rce.src.registerDetail', '{count} aksjonærer med til sammen {shares} aksjer', { count: capital.shareholderCount, shares: capital.shares })
      : t('rce.src.registerMissing', 'Ingen aksjonærer er registrert i Plumme.'),
  },
  {
    key: 'vedtekter',
    label: t('avbe.capital.articles', 'Vedtekter'),
    complete: Boolean(capital.articlesDocument),
    external: false,
    detail: capital.articlesDocument
      ? capital.articlesDocument.fileName
      : t('rce.src.articlesMissing', 'Vedtektene er ikke lastet opp på området.'),
  },
  {
    key: 'arsregnskap',
    label: t('rce.src.annual', 'Forrige års godkjente årsregnskap'),
    complete: disposition.previousResult !== null,
    external: false,
    detail: disposition.previousResult === null
      ? t('rce.src.annualMissing', 'Fjorårets resultat kunne ikke leses.')
      : t('rce.src.annualDetail', 'Årsresultat {year}: {result} kr', { year: disposition.previousYear, result: disposition.previousResult }),
  },
  {
    key: 'foretaksregisteret',
    label: t('rce.src.registry', 'Registrert aksjekapital og kapitalendringer (Foretaksregisteret)'),
    complete: false,
    external: true,
    detail: t('rce.src.registryDetail', 'Plumme henter ikke registrert aksjekapital eller kapitalendringer. Kontrolleres manuelt mot registeret.'),
  },
  {
    key: 'prognose',
    label: t('rce.src.forecast', 'Budsjett og likviditetsprognose'),
    complete: liquidity.budgetAvailable,
    external: !liquidity.budgetAvailable,
    detail: liquidity.budgetAvailable
      ? t('rce.src.forecastDetail', 'Budsjett finnes for året. Egen likviditetsprognose hentes ikke av appen.')
      : t('rce.src.forecastMissing', 'Verken budsjett eller likviditetsprognose er tilgjengelig for året.'),
  },
];

const equityReconChecks = ({ counts, statement, disposition }) => [
  {
    key: 'rullering',
    label: t('rce.check.rollforward', 'Inngående egenkapital pluss bevegelser er lik utgående'),
    value: statement.filter((row) => row.status === 'differanse').length,
  },
  { key: 'bilag', label: t('rce.check.vouchers', 'Bevegelsene har dekning i bilagene'), value: counts.bilagsdifferanse },
  { key: 'beslutning', label: t('rce.check.decision', 'Utdelinger har dokumentert beslutningsgrunnlag'), value: counts.mangler_beslutning },
  {
    key: 'disponering',
    label: t('rce.check.allocation', 'Bokført disponering summerer seg til forrige års resultat'),
    value: disposition.booked && !disposition.bookedMatchesResult ? 1 : 0,
  },
  { key: 'direktefort', label: t('rce.check.direct', 'Direkteføringer mot egenkapital er identifisert'), value: counts.direktefort },
  { key: 'eiere', label: t('rce.check.owners', 'Transaksjoner med eiere er identifisert'), value: counts.eiertransaksjon },
  { key: 'kapital', label: t('rce.check.capital', 'Selskapsformens kapitalkrav er bokført'), value: counts.aksjekapital_mangler },
  { key: 'negativ', label: t('rce.check.weak', 'Signaler om svak eller negativ egenkapital'), value: counts.negativ_egenkapital },
];

Object.assign(window, {
  EQUITY_RECON_FLAG,
  equityReconDecision,
  equityReconDisposition,
  equityReconCapital,
  equityReconLiquidity,
  equityReconItems,
  equityReconCounts,
  equityReconSources,
  equityReconChecks,
  equityReconDocumentMatch,
});
